/**
 * 小程序 `fetch` 适配（对应核心 SDK `types.ts` 里
 * `WorkBuddyCloudOptions.fetch` 的注入口子）。
 *
 * 用 `wx.request` 实现四模块共用的单一出口（`src/http/fetch.ts`
 * `createCloudFetch` 包出来的那个 fetch）。区分两种模式：
 *
 *  - 普通请求：`wx.request` 一次性拿到完整响应体。
 *  - 流式请求（LLM chat streaming，识别标志是请求头
 *    `Accept: text/event-stream`，见 `modules/llm/chat.ts:122`）：
 *    `enableChunked: true` + `onChunkReceived`，把收到的 chunk 喂进
 *    `polyfills.ts` 里的占位 `ReadableStream`，这样下游
 *    `iterSSEEvents`（`modules/llm/sse.ts`）完全不用改。
 */

import { ensureMiniProgramPolyfills, MiniProgramReadableStream } from './polyfills.js';
import type { MiniProgramWxLike, WxRequestOptions } from './wx-types.js';

function headersToPlainObject(headers?: HeadersInit): Record<string, string> {
    if (!headers) return {};
    if (headers instanceof Headers) {
        const out: Record<string, string> = {};
        headers.forEach((value, key) => {
            out[key] = value;
        });
        return out;
    }
    if (Array.isArray(headers)) {
        return Object.fromEntries(headers);
    }
    return { ...(headers as Record<string, string>) };
}

function isStreamingRequest(headers: Record<string, string>): boolean {
    const accept = headers['Accept'] ?? headers['accept'];
    return accept === 'text/event-stream';
}

/** SDK 各模块实际消费的 `Response` 字段面（完整 fetch 签名用类型断言补齐）。 */
interface ResponseLike {
    readonly status: number;
    readonly statusText: string;
    readonly ok: boolean;
    readonly headers: Headers;
    readonly body: MiniProgramReadableStream | null;
    text(): Promise<string>;
    json(): Promise<unknown>;
    blob(): Promise<Blob>;
    arrayBuffer(): Promise<ArrayBuffer>;
}

/**
 * 构造一个满足 SDK 实际用到的字段面的 `Response`-like 对象。
 *
 * 不走 `new Response(...)`：`ensureMiniProgramPolyfills()` 只补 `Headers` /
 * `ReadableStream`，**不**补 `Response` —— 真机小程序基础库没有全局
 * `Response` 时，构造函数路径会在第一个请求上直接 `ReferenceError`
 * （jsdom 自带 Response，单测环境暴露不出来）。SDK 对返回值的实际消费面：
 * auth/llm 的 `ok/status/text()`、database vendor 的
 * `ok/status/statusText/headers/json()`、LLM 流式的 `body.getReader()`。
 *
 * `blob()`/`arrayBuffer()` 目前**没有**真实实现：本适配层用
 * `responseType: 'text'` 拿响应体，天然丢失二进制内容，且小程序运行时
 * 没有全局 `Blob` 可用（`ensureMiniProgramPolyfills` 也没有补）。Storage
 * 模块的文件下载（`vendor/storage/packages/BlobDownloadBuilder.ts` 会
 * `await result.blob()`）在小程序环境下调这两个方法时，**必须**明确
 * reject 出一条可定位的错误，而不是让调用方撞见「result.blob is not a
 * function」这种无法一眼看出根因的 TypeError。真要支持小程序下载，需要
 * 重新设计整条链路（`wx.request({responseType:'arraybuffer'})` + 自制
 * 最小 Blob 替身 + 小程序侧没有 `URL.createObjectURL`/`<a download>`
 * 等浏览器 API，下游怎么消费也要重新设计），不是这里能补的一个方法。
 */
function buildResponse(params: {
    status: number;
    statusText?: string;
    headers: Record<string, string>;
    bodyText?: string;
    stream?: MiniProgramReadableStream;
}): Response {
    const status = params.status;
    const ok = status >= 200 && status < 300;
    const bodyText = params.bodyText ?? '';
    const unsupportedBinaryBody = (method: string) => (): Promise<never> =>
        Promise.reject(
            new Error(
                `createMiniProgramFetch: Response.${method}() 不受支持 —— 小程序适配层用文本方式取响应体` +
                    '，没有原始二进制数据，也没有全局 Blob 可用。依赖二进制下载的能力（如 Storage 文件下载）' +
                    '在小程序运行时暂不可用，需要单独设计后再接入。',
            ),
        );

    const response: ResponseLike = {
        status,
        statusText: params.statusText ?? '',
        ok,
        // 全局 Headers 由 ensureMiniProgramPolyfills 保证存在（原生或占位实现）。
        headers: new Headers(params.headers),
        // 非流式路径 SDK 只消费 text()/json()，body 置 null（与原生对无体
        // 响应的语义一致）；流式必须保留 MiniProgramReadableStream，
        // 下游 iterSSEEvents 直接对它 getReader()。
        body: params.stream ?? null,
        text: () => Promise.resolve(bodyText),
        // 解析失败要和原生 Response.json() 一致：返回 rejected promise
        // （SyntaxError），既不同步 throw，也不吞错。
        json: () =>
            new Promise<unknown>((resolve, reject) => {
                try {
                    resolve(JSON.parse(bodyText));
                } catch (error) {
                    // JSON.parse 按规范只抛 SyntaxError，原样透传即可。
                    reject(error);
                }
            }),
        blob: unsupportedBinaryBody('blob'),
        arrayBuffer: unsupportedBinaryBody('arrayBuffer'),
    };
    return response as unknown as Response;
}

/**
 * 创建小程序版 fetch，可直接传给 `WorkBuddyCloudOptions.fetch`。
 *
 * @param wxInstance 默认取全局 `wx`；传参主要用于单测注入 mock。
 */
export function createMiniProgramFetch(
    wxInstance: MiniProgramWxLike = (globalThis as { wx?: MiniProgramWxLike }).wx as MiniProgramWxLike,
): typeof globalThis.fetch {
    if (!wxInstance) {
        throw new Error(
            'createMiniProgramFetch: 未找到全局 wx 对象，请在小程序环境调用，或显式传入 wxInstance。',
        );
    }

    ensureMiniProgramPolyfills();

    return async function miniProgramFetch(
        input: RequestInfo | URL,
        init?: RequestInit,
    ): Promise<Response> {
        if (typeof input !== 'string') {
            // 核心 SDK 目前只传字符串 URL（见 `http/fetch.ts` 的调用面），
            // Request/URL 对象输入暂不支持 —— 真出现时再补，先明确报错好定位。
            throw new Error(
                'createMiniProgramFetch: 暂不支持非字符串 input，请传 URL 字符串。',
            );
        }

        const url = input;
        const method = (init?.method ?? 'GET').toUpperCase();
        const reqHeaders = headersToPlainObject(init?.headers);
        const streaming = isStreamingRequest(reqHeaders);

        let requestBody: string | ArrayBuffer | Record<string, unknown> | undefined;
        if (typeof init?.body === 'string') {
            requestBody = init.body;
        } else if (init?.body !== undefined && init?.body !== null) {
            // ArrayBuffer/Blob/FormData 等暂不支持 —— 四模块目前请求体都是
            // JSON.stringify 出来的字符串，真遇到再补（占位）。
            throw new Error(
                'createMiniProgramFetch: 暂不支持非字符串 body（ArrayBuffer/FormData 待补）。',
            );
        }

        if (init?.signal?.aborted) {
            const err = new Error('The operation was aborted.');
            err.name = 'AbortError';
            throw err;
        }

        if (!streaming) {
            return new Promise<Response>((resolve, reject) => {
                const task = wxInstance.request({
                    url,
                    method,
                    header: reqHeaders,
                    data: requestBody,
                    responseType: 'text',
                    success(result) {
                        resolve(
                            buildResponse({
                                status: result.statusCode,
                                headers: result.header,
                                bodyText:
                                    typeof result.data === 'string'
                                        ? result.data
                                        : JSON.stringify(result.data),
                            }),
                        );
                    },
                    fail(result) {
                        reject(new Error(`wx.request failed: ${result.errMsg}`));
                    },
                });

                init?.signal?.addEventListener(
                    'abort',
                    () => {
                        task.abort();
                        const err = new Error('The operation was aborted.');
                        err.name = 'AbortError';
                        reject(err);
                    },
                    { once: true },
                );
            });
        }

        // 流式路径：先拿到 header（通过一次 success 回调里带的 statusCode/header，
        // 但 wx.request 分块模式下 success 在整个请求结束（流结束）才触发 ——
        // 这与 fetch 语义不同：fetch 的 Response 应该在收到 header 后立即可用，
        // body 再逐步可读。
        //
        // ⚠️ 占位说明：这是本适配层最大的语义落差，未在真机验证。微信小程序
        // 官方文档 RequestTask.onHeadersReceived 只带 header、不带
        // statusCode，因此「首个 chunk 到达时就 resolve Response」这一步无法
        // 拿到真实状态码，只能先假设 200——如果后续发现真机行为不一致，需要
        // 重新设计这一段。但这不代表可以对真实的非 2xx 结果视而不见：
        // success/fail 回调始终会在流结束时真正触发一次，带着准确的
        // statusCode——如果那时发现不是 2xx，或者 fail 触发，必须让
        // stream.abort() 而不是 stream.close()，否则调用方会把一段被
        // 截断的错误响应体误判成正常收完的 SSE 内容。
        return new Promise<Response>((resolve, reject) => {
            const stream = new MiniProgramReadableStream();
            let resolved = false;
            let receivedHeaders: Record<string, string> = {};
            const options: WxRequestOptions = {
                url,
                method,
                header: reqHeaders,
                data: requestBody,
                enableChunked: true,
                success(result) {
                    const isSuccessStatus = result.statusCode >= 200 && result.statusCode < 300;
                    if (!resolved) {
                        resolved = true;
                        resolve(
                            buildResponse({
                                status: result.statusCode,
                                headers: result.header,
                                stream,
                            }),
                        );
                        stream.close();
                        return;
                    }
                    // 已经用假设的 200 提前 resolve 过 Response：这是唯一能拿到
                    // 真实 statusCode 的时机。非 2xx 说明整段响应实际是错误，
                    // 即使已经收到过 chunk，也不能悄悄当正常结束处理。
                    if (isSuccessStatus) {
                        stream.close();
                    } else {
                        stream.abort(
                            new Error(`wx.request (chunked) resolved with non-2xx status ${result.statusCode}`),
                        );
                    }
                },
                fail(result) {
                    const err = new Error(`wx.request (chunked) failed: ${result.errMsg}`);
                    if (!resolved) {
                        resolved = true;
                        reject(err);
                        return;
                    }
                    // 已经开始流式返回数据后网络异常/请求被终止：必须让 reader
                    // 感知到错误（abort），而不是当成正常 EOF（close）悄悄结束。
                    stream.abort(err);
                },
            };
            const task = wxInstance.request(options);
            task.onHeadersReceived?.((headerResult) => {
                receivedHeaders = headerResult.header ?? {};
            });

            task.onChunkReceived((chunk) => {
                if (!resolved) {
                    resolved = true;
                    resolve(
                        buildResponse({
                            status: 200,
                            headers: receivedHeaders,
                            stream,
                        }),
                    );
                }
                stream.push(new Uint8Array(chunk.data));
            });

            init?.signal?.addEventListener(
                'abort',
                () => {
                    task.abort();
                    const err = new Error('The operation was aborted.');
                    err.name = 'AbortError';
                    if (!resolved) {
                        resolved = true;
                        reject(err);
                        return;
                    }
                    stream.abort(err);
                },
                { once: true },
            );
        });
    };
}
